import { Anchor, Breadcrumbs, Tabs } from '@mantine/core';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import GoogleAdDetailsForm from '../../components/Google/Ads/GoogleAdDetailsFrom';
import GoogleAdRecommendations from '../../components/Google/Ads/GoogleAdRecommendations';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import GuidanceModal from '../../components/UserFlows/guidanceModal';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';

const page = `${Page['googleAds']}`;

const GoogleAdsDetailsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const [googleAd, setGoogleAd] = useState();
  const [loadError, setLoadError] = useState();
  const [guidanceShow, setGuidanceShow] = useState(false);
  const [useInAutomation, setUseInAutomation] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'recommendations' ? 'recommendations' : 'settings';
  const setActiveTab = useCallback(
    (value) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (value === 'settings') next.delete('tab');
        else next.set('tab', value);
        return next;
      });
    },
    [setSearchParams]
  );
  const switchToSettings = useCallback(() => setActiveTab('settings'), [setActiveTab]);

  const { accountId, domainId, googleAdId } = useParams();

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/googleAds/${googleAdId}`)
      .then(({ googleAd }) => {
        if (!googleAd) {
          navigate(`/accounts/${accountId}/domains/${domainId}/google/ads`);
          return;
        }

        setGoogleAd(googleAd);
      })
      .catch(setLoadError);
  }, [fetch, navigate, accountId, domainId, googleAdId]);

  const close = () => {
    setGuidanceShow(false);
    navigate(`/accounts/${accountId}/domains/${domainId}/google/ads`);
  };

  const showUserFlow = () => {
    setGuidanceShow(true);
    setUseInAutomation(true);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (googleAd === undefined) {
    return <Spinner />;
  }

  const name = googleAd?.name || 'Unknown Ad';

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/google/ads` },
    { title: name },
  ].map((item, index) => (
    <Anchor href={item.href} key={index}>
      {item.title}
    </Anchor>
  ));

  return (
    <>
      <Metadata title={`${page} | ${name} | Edit`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>
      <GuidanceModal
        type="Google Ad"
        redirect={`/accounts/${accountId}/domains/${domainId}/google/ads`}
        show={guidanceShow}
        elementTypes={['campaigns', 'workflows']}
        element={googleAd}
        setShow={() => {
          setGuidanceShow(false);
          setUseInAutomation(false);
        }}
        useInAutomation={useInAutomation}
      />
      {googleAd === null ? (
        <ErrorMessage message="Google Ad has been deleted." />
      ) : (
        <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
          <Tabs.List mb="md">
            <Tabs.Tab value="settings">Campaign Settings</Tabs.Tab>
            <Tabs.Tab value="recommendations">Recommendations</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="settings">
            <GoogleAdDetailsForm
              googleAd={googleAd}
              close={close}
              onDuplicate={() =>
                navigate(
                  location.pathname.replace(
                    `/google/ads/${googleAd.id}/details`,
                    `/google/ads/new?from=${googleAd.id}`
                  )
                )
              }
              onBack={() => navigate(-1)}
              showUserFlow={() => showUserFlow(true)}
            />
          </Tabs.Panel>

          <Tabs.Panel value="recommendations">
            <GoogleAdRecommendations googleAd={googleAd} onSwitchToSettings={switchToSettings} />
          </Tabs.Panel>
        </Tabs>
      )}
    </>
  );
};

export default GoogleAdsDetailsPage;
